import React, { useMemo } from 'react'
import { useQuery } from 'jsonapi-react'
import { Helmet } from 'react-helmet-async'
import pluralize from 'pluralize'
import { Layout, Sidebar, TwoColumns } from 'app/theme/layout'
import Breadcrumbs from 'app/theme/Breadcrumbs'
import CityDropdown from 'app/theme/CityDropdown'
import MapListToggle from 'app/theme/MapListToggle'
import PillToggle from 'app/theme/PillToggle'
import EventMap from 'app/theme/EventMap'
import BookTruckBanner from 'app/theme/banners/BookTruckBar'
import ShadowBox from 'app/caterers/ShadowBox'
import Pagination from 'app/theme/Pagination'
import ErrorPage from 'app/pages/ErrorPage'
import NotFoundPage from 'app/pages/NotFoundPage'
import HtmlContent from 'app/theme/HtmlContent'
import { format } from 'date-fns'
import { useSearchParams } from 'app/theme/useSearchParams'
import { useLocation, Redirect } from 'react-router-dom'

const monthYear = format(new Date(), 'MMMM yyyy')

export default function RegionPage({ region, page, tagSlug }) {
  const path = `/food-trucks/${region.url_slug}`
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const searchTerm = searchParams.search

  const query = useQuery(['caterers', {
    sort: '-ranking',
    filter: {
      region_id: region.id,
      tag_slug: tagSlug || undefined,
      closed: false,
      search: searchTerm
    },
    include: [
      'photo',
    ],
    fields: {
      caterers: ['id', 'name', 'slug'],
      photo: ['uuid', 'extension', 'path']
    },
    page: {
      number: page || 1,
      size: 21
    },
    stats: {
      total: 'count',
    }
  }])

  const { data: [tag] = [] } = useQuery(tagSlug && ['tags', {
    filter: {
      slug: tagSlug,
    },
    fields: {
      tags: ['name', 'slug', 'primary', 'city_description', 'region_description']
    }
  }])

  const crumbs = [
    { text: 'Food Trucks', url: `/food-trucks/` },
  ]

  if (tag) {
    crumbs.push({ text: region.name, url: `${path}/1/` })
    crumbs.push({ text: tag.name, url: searchTerm ? `/food-trucks/${region.url_slug}/${tag.slug}/` : null })
  } else {
    crumbs.push({ text: region.name, url: searchTerm ? `/food-trucks/${region.url_slug}/` : null })
  }

  if (searchTerm) {
    crumbs.push({ text: `Search: ${searchTerm}`})
  }

  const isMapPage = !page && !tagSlug

  let caterers = query.data ? [...query.data] : []

  const googleMetaTags = useMemo(() => {
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'itemListElement': caterers.map((caterer, index) => ({
        '@type': 'ListItem',
        position: index,
        url: `https://roaminghunger.com/${caterer.slug}/`
      }))
    })
  }, [caterers])

  if (query.isLoading) {
    return null
  }

  if (query.error && query.error.code !== 'not_found') {
    return <ErrorPage />
  }

  if (!query.data || !query.data.length) {
    return <NotFoundPage />
  }

  const tagNameSingular = tag ? pluralize.singular(tag?.name) : null

  if (caterers.length && page || tagSlug) {
    caterers.splice(3, 0, 'banner')
  }

  const count = query.meta?.stats?.total?.count
  const pageOutOfRange = !searchTerm && (page && Math.ceil(count / 21) < page)

  const metaTitleCount = !searchTerm && (!page && count >= 5) ? (count > 21 ? "The 21 Best" : `The ${count} Best`) : ''

  let metaTitle
  let metaDescription
  let metaOgTitle

  if (isMapPage) {
    metaTitle = `Food Truck Map - ${region.name} - Catering & Events - Roaming Hunger`
    metaDescription = `Locate the best Food Trucks near you in ${region.name} and find the perfect food truck to cater your office, party, wedding or next event.`
    metaOgTitle = `Food Truck Map - ${region.name} - Catering & Events`
  } else {
    metaTitle = `${metaTitleCount} ${region.name} ${tag ? tagNameSingular : ""} Food Trucks (${monthYear}) - Roaming Hunger`
    metaDescription = `Find the best ${tag?.name || ""} Food Trucks in ${region.name} and book or rent a ${tag?.name || ""} food truck, trailer, cart, or pop-up for your next catering, party or event.`
    metaOgTitle = `${metaTitleCount} ${region.name} Food Trucks (${monthYear})`
  }

  const parseTagRegionDescription = () => {
    let base = tag.region_description
    base = base.replaceAll("{{REGION_NAME}}", region.name)
    base = base.replaceAll("{{REGION_ABBREV}}", region.code)
    base = base.replaceAll("{{COUNT}}", query.meta?.stats?.total?.count)
    return base
  }

  if (count && page && pageOutOfRange) {
    // If I try to go to page 8 and there is only 1 page's worth of search results
    // We used to show a 404 page, but now
    // we will redirect to the first page of results
    return <Redirect to={`/food-trucks/${region.url_slug}/1/`} />
  }

  return (
    <Layout className="container py-12">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription}></meta>
        <meta name="og:title" content={metaOgTitle}></meta>
        <script type="application/ld+json">
          {googleMetaTags}
        </script>
        {!count || count < 2 ? (
          <meta name="robots" content="noindex" />
        ) : null }
      </Helmet>
      <TwoColumns sidebar={<Sidebar region={region} />}>
        <Breadcrumbs
          crumbs={crumbs}
        />
        <div className="mt-4 flex items-center">
          <h1 className="text-3xl lg:text-4xl text-navy font-platform leading-tight">
            {tag ? `${region.name} ${tagNameSingular} Trucks` : `${region.name} Food Trucks`}
          </h1>
          <CityDropdown
            cities={region.cities.filter(c => c.caterer_count > 0)}
            className="ml-3 mt-2"
          />
        </div>
        <div>
          {region.short_description}
        </div>
        {isMapPage ? (
          <div>
            <div className="sm:flex justify-between items-center pt-4 mt-4 mb-1 border-t border-gray-300">
              <div className="text-navy text-2xl font-platform">
                Live Map
              </div>
              <MapListToggle
                active="map"
                mapUrl={`${path}/`}
                listUrl={`${path}/1/`}
              />
            </div>
            <EventMap
              lat={region.location.lat}
              lng={region.location.lng}
              center={`${region.name}, ${region.country.name}`}
              boundingBox={region.bounding_box}
            />
            <BookTruckBanner
              className="mt-6"
              url={`/${region.url_slug}/food-truck-catering/`}
            />
            <div className="mt-8 mb-5 text-navy text-4xl font-platform">
              Food Trucks
            </div>
            {caterers.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {caterers.map(caterer => (
                  <ShadowBox
                    key={caterer.id}
                    caterer={caterer}
                    photo={caterer.photo}
                  />
                ))}
              </div>
            ) : (
              <div className="text-lg italic font-light">
                Sorry, we couldn't find any {tagNameSingular || ""} Food Trucks or {tagNameSingular || ""} Food Carts in {region.name}.
              </div>
            )}
          </div>
        ) : (
          <div>
            <HtmlContent
              className="mt-3"
              content={
                tag?.region_description ? parseTagRegionDescription() : region.short_description
              }
            />
            <div
              className={[
                'justify-between items-center mt-4 pt-4 pb-3',
                'border-t border-gray-300',
                'sm:flex sm:pb-1 md:mb-2'
              ]}
            >
              <MapListToggle
                active="list"
                mapUrl={`${path}/`}
                listUrl={`${path}/1/`}
                className="sm:order-2"
              />
              <PillToggle
                pills={[
                  { text: 'Preferred', url: `${path}/1/`, active: !tagSlug },
                  { text: 'Savory', url: `${path}/savory/`, active: tagSlug === 'savory' },
                  { text: 'Sweet', url: `${path}/sweet/`, active: tagSlug === 'sweet' },
                  { text: 'Vegetarian', url: `${path}/vegetarian/`, active: tagSlug === 'vegetarian' },
                ]}
              />
            </div>

            <h2 className="text-navy leading-none text-xl mb-6 md:text-2xl">
              The best {tagNameSingular || ""} Food Trucks and {tagNameSingular || ""} Food Carts in {region.name}
            </h2>

            {caterers.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 lg:gap-6">
                {caterers.map((caterer, index) => (
                  caterer === 'banner' ? (
                    <div key="banner" className="hidden md:flex md:col-span-3">
                      <BookTruckBanner url={`/${region.url_slug}/food-truck-catering/`}/>
                    </div>
                  ) : (
                    <ShadowBox
                      key={caterer.id}
                      caterer={caterer}
                      photo={caterer.photo}
                    />
                  )
                ))}
              </div>
            ) : (
              <div className="text-lg italic font-light">
                Sorry, we couldn't find any {tagNameSingular || ""} Food Trucks or {tagNameSingular || ""} Food Carts in {region.name}.
              </div>
            )}
            
            {query.meta && (
              <Pagination
                className="justify-center mt-10"
                total={query.meta.stats.total.count}
                current={page || 1}
                limit={21}
                getLinkProps={link => ({
                  href: tag
                    ? `${path}/${tag.slug}/${link.page === 1 ? '' : `${link.page}/`}${location.search || ""}`
                    : `${path}/${link.page}/${location.search || ""}`
                })}
              />
            )}
          </div>
        )}
        {searchTerm && !caterers.length ? (
          <p>Sorry, we couldn't find any trucks or carts matching "{searchTerm}".</p>
        ) : null}
        {page === 1 && !caterers.length ? (
          <p>No Caterers Found</p>
        ) : null}
      </TwoColumns>
    </Layout>
  )
}
